// HU-50 · Bandeja de Asuntos Regulatorios (F-05, Sprint 2; rev. 3, 21 criterios) y la
// parte visible de HU-24 (alertas de vencimiento del plazo regulatorio).
//
// LA BANDEJA PRIORIZA, NO ESCONDE (RN-35). Es la hermana de la bandeja del Comité: mismo
// principio, otros bloques y otro reloj —el PLAZO REGULATORIO ante la autoridad (RN-20/21)—:
//
//   1  Vencidos                        «Plazo regulatorio vencido», texto + ícono (C7)
//   2  Por vencer                      «Vence mañana» cuando el siguiente día hábil es el límite (HU-24-C5)
//   3  En plazo                        el más antiguo primero (C4)
//   4  Esperando respuesta de afuera   la firma del PRFV o el cargo de la autoridad (C11);
//                                      «Acuse demorado» dentro de la espera (C12)
//   5  Informe final pendiente         notificación cumplida, falta remitir el FAR-03 (C13)
//   ·  Sin plazo configurado           aparte y NUNCA «en plazo» (C10, RN-21)
//
// El orden, la clasificación y los DOS relojes (notificación e informe final, HU-24-C10)
// vienen del servidor (`GET /regulatorio/bandeja`); aquí no se reordena ni se recalcula.
// Los filtros viajan como query string y se combinan con Y (C14–C16d); al filtrar se
// conservan los bloques y desaparecen los vacíos (C17).
//
// LA ACCIÓN DE LA FILA ABRE LA TAREA, DIRECTO (C5, 14-sep-2026). «Completar el Formato
// Amarillo» abre el Formato Amarillo; «Preparar la carta», «Solicitar la firma»,
// «Registrar el cargo»… abren la carta; «Remitir el informe final» abre el trámite, que es
// donde se registra (HU-24-C12). Antes todas pasaban por la pantalla intermedia del
// trámite, que sigue existiendo —«Ver trámite» en cada fila— con los relojes, el estado
// paso a paso y el cierre del caso (HU-22-C5).
//
// CON ÁMBITO (C19, RNF-08): el backend acota la bandeja y «Ver todos los casos» (C20) a los
// países del actor; la pantalla no ofrece filtro de país porque no hay nada que elegir.
//
// CC-02 · UNA FILA POR TRÁMITE CON EL ESTADO DE CADA CARTA (B10, AT S3 §11.3.8,
// `ui/MG-ILE-1076.md`). Con varias cartas la fila lista el sub-estado de cada una tal como lo
// escribe el servidor («SENASA: enviada, cargo pendiente»), la columna de autoridad nombra los
// destinos y el paso es el de la carta más urgente, seguido de «y 1 carta más». La espera de
// firma del PRFV es una fila POR CARTA (CC2-50-C2). Con una sola carta la fila es la de siempre.

import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { comoFechaDeInstante, fechaDeHoyEnZona } from '../lib/fechas';
import type { ReactNode } from 'react';
import {
  AlertTriangle, ArrowLeft, CheckCircle2, FileCheck2, FileText, FolderOpen, Hourglass, Inbox,
  ListChecks, Lock, RefreshCw, Search, Send, TimerOff, X,
} from 'lucide-react';
import {
  Alert, Button, Card, Codigo, EmptyState, Field, Input, Modal, Mono, PageHeader, Select, StatusBadge, Stepper, Textarea,
} from '../components/ui';
import type { Tono } from '../components/ui';
import Bandera from '../components/Banderas';
import FormatoAmarillo from './FormatoAmarillo';
import CartaDeNotificacion from './CartaDeNotificacion';
import {
  ACCIONES_REGULATORIAS, ESTADOS_DEL_RELOJ, MENSAJE_CARTA_DESACTUALIZADA, buscarCasoRegulatorio, cerrarCaso,
  consultarBandejaRegulatoria, estadoDelCierre, etiquetaDeLaCarta, formatoAmarilloDeLaCartaVigente, prepararCarta,
  registrarRemisionDelInformeFinal, verTramite,
} from '../api/regulatorio';
import type {
  AccionRegulatoria, BandejaRegulatoria as Bandeja, BusquedaRegulatoria, CartaDeLaFila, CasoRegulatorioEncontrado,
  ClaveDeBloqueRegulatorio, EstadoDelCierre, EstadoDelReloj, FilaRegulatoria, FiltrosRegulatorios, Tramite,
} from '../api/regulatorio';
import { aFallo } from '../api/cliente';
import type { Fallo } from '../api/cliente';
import { MENSAJE_RANGO_INVERTIDO, rangoInvertido } from '../lib/entradas';
import { etiquetaDelFormatoAmarillo } from '../lib/etiquetas';
// BUG-O11 · el acuse se pinta con el texto del servidor, con la misma insignia que el expediente.
import EstadoDelAcuse from '../components/EstadoDelAcuse';
// El reloj del plazo, el mismo que pinta cada carta (CC-02, B11).
import RelojDelPlazo from '../components/RelojDelPlazo';
import { FilasDeLasCartas, RemisionPorCarta } from '../components/EstadoDeCadaCarta';
import { cartasActivas } from '../api/cartas';
import { filtrosIniciales, guardarFiltros } from '../lib/filtrosPersistidos';
import RangoDeFechas from '../components/ui/RangoDeFechas';
import FiltrosColapsables from '../components/ui/FiltrosColapsables';
import Paginacion, { POR_PAGINA_DE_LISTADOS, paginar, paginasDe } from '../components/ui/Paginacion';
import { useDiferido } from '../lib/useDiferido';
import { useZonaDelPais } from '../lib/CatalogosContexto';

// DD/MM/AAAA EN TODA LA INTERFAZ (25-sep-2026). Antes recortaba el ISO y lo mostraba
// tal cual, asi que en la misma fila convivian «2026-09-24» y «25/09/26». RN-82: el día se
// toma en la zona del país del caso.
const fecha = (iso: unknown, zona: string | null): string => comoFechaDeInstante(iso, zona) || '—';

/**
 * El estado de la carta como lo lee la FILA: «Carta enviada», «Carta sin preparar». Sale
 * del mismo mapa que la pantalla de la carta y el expediente; solo se le antepone el
 * sustantivo, porque en la celda no hay rótulo que diga de qué es el estado.
 */
const cartaEnLaFila = (estado: string): string => {
  if (estado === 'esperando_firma') return etiquetaDeLaCarta(estado);
  const etiqueta = etiquetaDeLaCarta(estado);
  return `Carta ${etiqueta.charAt(0).toLowerCase()}${etiqueta.slice(1)}`;
};

/**
 * Lo que dice la insignia de una fila de «Ver todos los casos» (HU-50-C20).
 *
 * Tres lecturas, no dos. Hasta el bug 702 esa búsqueda solo alcanzaba a los casos CON
 * trámite regulatorio, así que «concluido» o «el estado de la carta» agotaban los casos
 * posibles. Ahora alcanza también a los NO GRAVES, que no tienen ruta regulatoria en
 * absoluto (RN-15): para ellos «Carta sin preparar» anuncia un trabajo pendiente que
 * nadie va a hacer nunca, y en una bandeja de trabajo eso es una instrucción falsa.
 */
const estadoDeLaFilaEncontrada = (c: CasoRegulatorioEncontrado): string => {
  // O-07: «concluido» es la NOTIFICACIÓN (carta enviada + cargo), no todo el caso: el
  // FAR-03 puede seguir sin remitir y el caso sin cerrar. La palabra lo dice ahora.
  if (c.tramite_concluido) return 'Notificación concluida';
  if (c.gravedad === 'no_grave') return 'Sin ruta regulatoria';
  return cartaEnLaFila(c.estado_carta);
};

const tonoDeLaFilaEncontrada = (c: CasoRegulatorioEncontrado): Tono => {
  if (c.tramite_concluido) return 'success';
  return c.gravedad === 'no_grave' ? 'neutral' : 'info';
};

// HU-20 a HU-22: los pasos que se resuelven en la pantalla de la carta, con su verbo.
const PASOS_DE_LA_CARTA: Record<string, string> = {
  preparar_la_carta: 'Preparar la carta',
  solicitar_la_firma: 'Solicitar la firma',
  esperar_la_firma: 'Registrar la firma del PRFV',
  presentar_la_carta: 'Presentar la carta',
  registrar_el_cargo: 'Registrar el cargo',
};

/** Qué pantalla resuelve cada acción pendiente de la fila (C5). */
type Tarea = 'formato' | 'carta' | 'tramite';
const TAREA_DE: Record<AccionRegulatoria, Tarea> = {
  completar_formato_amarillo: 'formato',
  preparar_la_carta: 'carta',
  solicitar_la_firma: 'carta',
  esperar_la_firma: 'carta',
  presentar_la_carta: 'carta',
  registrar_el_cargo: 'carta',
  remitir_el_informe_final: 'tramite',
};

/**
 * CC-02 (B10, CC2-50-C1): el estado de cada carta bajo el número de caso, con el texto del
 * servidor, y el aviso de plazo prestado de la que lo tenga (CC2-24-C2). Con una sola carta,
 * el estado de siempre («Carta enviada») y, si su plazo es prestado, el aviso.
 */
function EstadoDeLasCartas({ fila }: Readonly<{ fila: FilaRegulatoria }>) {
  const cartas: CartaDeLaFila[] = fila.cartas ?? [];
  if (cartas.length > 1) {
    return (
      <ul className="mt-0.5 flex min-w-[11rem] flex-col gap-0.5" data-testid="bandeja-reg-cartas" aria-label={`Cartas de ${fila.numero ?? 'el caso'}`}>
        {cartas.map((c) => (
          <li key={c.autoridad_codigo ?? c.texto} className="text-meta text-ink-soft" data-testid="bandeja-reg-carta" data-autoridad={c.autoridad_codigo ?? ''}>
            {c.texto}
            {c.texto_de_plazo_prestado && (
              <span className="block text-amber-700" data-testid="bandeja-reg-plazo-prestado">{c.texto_de_plazo_prestado}</span>
            )}
          </li>
        ))}
      </ul>
    );
  }
  return (
    <>
      <div className="text-meta text-ink-soft mt-0.5">{cartaEnLaFila(fila.estado_carta)}</div>
      {fila.texto_de_plazo_prestado && (
        <div className="text-meta text-amber-700" data-testid="bandeja-reg-plazo-prestado">{fila.texto_de_plazo_prestado}</div>
      )}
    </>
  );
}

/** C6: las ocho columnas. C12: el acuse demorado se distingue dentro de la espera. */
// `memo`: la tabla no se re-pinta con cada tecla de un filtro; solo cuando cambia la bandeja.
const FilaDelBloque = memo(function FilaDelBloque({ fila, onAccion, onTramite, onAbrir }: {
  fila: FilaRegulatoria; onAccion: (fila: FilaRegulatoria) => void; onTramite: (fila: FilaRegulatoria) => void;
  onAbrir?: (casoId: string) => void;
}) {
  const zona = useZonaDelPais()(fila.pais);
  return (
    <tr className="border-t border-hairline hover:bg-slate-50" data-testid="bandeja-reg-fila">
      <td className="px-4 py-2.5 align-top">
        <Codigo className="whitespace-nowrap">{fila.numero ?? fila.caso_id.slice(0, 8)}</Codigo>
        <EstadoDeLasCartas fila={fila} />
      </td>
      <td className="px-4 py-2.5 align-top">
        <span className="inline-flex items-center gap-2 text-compacto"><Bandera cod={fila.pais} nombre={fila.nombre_pais} />{fila.nombre_pais}</span>
      </td>
      <td className="px-4 py-2.5 align-top">{fila.autoridad ?? <span className="text-ink-soft">—</span>}</td>
      <td className="px-4 py-2.5 align-top">{fila.producto ?? <span className="text-ink-soft">—</span>}</td>
      <td className="px-4 py-2.5 align-top whitespace-nowrap"><Mono className="text-meta">{fecha(fila.fecha_recepcion, zona)}</Mono></td>
      <td className="px-4 py-2.5 align-top whitespace-nowrap">
        <Mono className="text-meta">{fecha(fila.notificacion.fecha_limite, zona)}</Mono>
      </td>
      <td className="px-4 py-2.5 align-top">
        <div className="flex flex-col gap-1.5">
          <RelojDelPlazo etiqueta="Notificación" reloj={fila.notificacion} zonaHoraria={zona} />
          {fila.informe_final && <RelojDelPlazo etiqueta="Informe final" reloj={fila.informe_final} zonaHoraria={zona} />}
          {/* HU-22-C2/C3: «Acuse pendiente» desde la carta enviada; «Acuse demorado» pasados N días hábiles. */}
          {fila.texto_del_acuse && fila.estado_cargo !== 'recibido' && (
            <StatusBadge tone={fila.acuse_demorado ? 'danger' : 'warning'} icon={Hourglass}>{fila.texto_del_acuse}</StatusBadge>
          )}
          {/* HU-21-C8: los días hábiles esperando la firma del PRFV. */}
          {fila.dias_esperando_firma !== null && fila.dias_esperando_firma !== undefined && (
            <StatusBadge tone="info" icon={Hourglass}>Esperando firma del PRFV · {fila.dias_esperando_firma} {fila.dias_esperando_firma === 1 ? 'día hábil' : 'días hábiles'}</StatusBadge>
          )}
        </div>
      </td>
      <td className="px-4 py-2.5 align-top text-right whitespace-nowrap">
        <span className="inline-flex flex-wrap justify-end gap-2">
          {/* C5: al pulsar la acción se abre ESA tarea para ese caso. */}
          {fila.accion && (
            <Button size="sm" variant="primary" icon={Send} onClick={() => onAccion(fila)}
              aria-label={`${fila.texto_de_la_accion} · ${fila.numero ?? 'el caso'}`}>
              {fila.texto_de_la_accion}
            </Button>
          )}
          {/* CC-02 (§11.3.8): otras cartas del trámite también esperan un paso. */}
          {fila.accion && fila.texto_de_mas_cartas && (
            <span className="self-center text-meta text-ink-soft" data-testid="bandeja-reg-mas-cartas">{fila.texto_de_mas_cartas}</span>
          )}
          <Button size="sm" variant="secondary" icon={ListChecks} onClick={() => onTramite(fila)}
            aria-label={`Ver trámite de ${fila.numero ?? 'el caso'}`}>
            Ver trámite
          </Button>
          {onAbrir && (
            <Button size="sm" variant="secondary" icon={FolderOpen} onClick={() => onAbrir(fila.caso_id)}
              aria-label={`Abrir ${fila.numero ?? 'el caso'} en solo lectura`}>
              Abrir
            </Button>
          )}
        </span>
      </td>
    </tr>
  );
});

const COLUMNAS = ['N.º de caso', 'País', 'Autoridad', 'Producto', 'Recibido', 'Fecha límite', 'Estado del plazo', 'Acción pendiente'];

/**
 * C2: un bloque con su encabezado «Vencidos (2)». Solo se pinta si tiene filas (C17).
 *
 * DE 10 EN 10 POR BLOQUE (operador, 5-oct-2026: «los listados paginan de 10 en 10»). El
 * encabezado sigue contando el bloque entero; la tabla pinta solo la página y, si el bloque
 * tiene más de 10, lleva su propio pie. El orden es el del servidor: aquí solo se trocea.
 */
function Bloque({ bloque, pagina: paginaPedida, onPagina, onAccion, onTramite, onAbrir }: {
  bloque: Bandeja['bloques'][number]; pagina: number; onPagina: (clave: ClaveDeBloqueRegulatorio, pagina: number) => void;
  onAccion: (fila: FilaRegulatoria) => void; onTramite: (fila: FilaRegulatoria) => void;
  onAbrir?: (casoId: string) => void;
}) {
  const total = bloque.filas.length;
  const pagina = Math.min(Math.max(1, paginaPedida), paginasDe(total));
  const filas = paginar(bloque.filas, pagina);
  return (
    <Card pad={false} className="mb-4" title={`${bloque.titulo} (${bloque.cuenta})`}>
      {/* La seccion con nombre ES la zona que desplaza (WCAG 1.4.10 y 2.1.1, auditoria del 2-oct-2026).
          `relative`: recorta lo `absolute` de sus celdas (un `sr-only`), que si no agranda la pagina (BUG-1099). */}
      <section aria-label={`${bloque.titulo} (${bloque.cuenta})`} className="relative overflow-x-auto max-h-[70vh]">
        <div>
          <table className="w-full text-compacto">
            <thead className="bg-canvas sticky top-0 z-[1]">
              <tr>
                {COLUMNAS.map((c) => (
                  <th key={c} scope="col"
                    className="font-semibold text-meta text-ink-soft px-4 py-2.5 whitespace-nowrap text-left">
                    {c}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filas.map((f) => <FilaDelBloque key={f.caso_id} fila={f} onAccion={onAccion} onTramite={onTramite} onAbrir={onAbrir} />)}
            </tbody>
          </table>
        </div>
      </section>
      {total > POR_PAGINA_DE_LISTADOS && (
        <div className="px-4 py-3 border-t border-hairline">
          <Paginacion pagina={pagina} total={total} enEstaPagina={filas.length}
            onPagina={(p) => onPagina(bloque.clave, p)} etiqueta={`Paginación de ${bloque.titulo}`}
            sustantivo={['caso', 'casos']} data-testid={`regulatorio-bloque-${bloque.clave}-paginacion`} />
        </div>
      )}
    </Card>
  );
}

/**
 * C20 · «Ver todos los casos». Un caso que ya cerró su trámite sigue siendo alcanzable por
 * su número, dentro del ámbito (C19). Se monta ENCIMA de la bandeja: al volver, los
 * filtros y el orden siguen tal cual.
 */
function TodosLosCasos({ onAbrir, onVolver }: { onAbrir?: (casoId: string) => void; onVolver: () => void }) {
  const [numero, setNumero] = useState('');
  const [resultado, setResultado] = useState<BusquedaRegulatoria | null>(null);
  const [fallo, setFallo] = useState<Fallo | null>(null);
  const [buscando, setBuscando] = useState(false);
  // Los resultados (hasta el tope del servidor) se ven de 10 en 10 (operador, 5-oct-2026).
  const [paginaDeResultados, setPaginaDeResultados] = useState(1);

  const buscar = async () => {
    if (numero.trim().length < 2) return;
    setBuscando(true);
    setFallo(null);
    try {
      setPaginaDeResultados(1);
      setResultado(await buscarCasoRegulatorio(numero));
    } catch (e) {
      setFallo(aFallo(e));
    } finally {
      setBuscando(false);
    }
  };

  return (
    <>
      <PageHeader
        eyebrow="Asuntos Regulatorios · Todos los casos"
        hu="HU-50 · C20"
        title="Todos los casos"
        subtitle="Busca por número un caso de tu ámbito que ya no está en la bandeja (notificación concluida y FAR-03 remitido) y ábrelo en solo lectura."
        icon={Search}
        actions={<Button variant="secondary" size="sm" icon={ArrowLeft} onClick={onVolver}>Volver a la bandeja</Button>}
      />
      <Card>
        <form className="flex flex-wrap items-end gap-3" onSubmit={(e) => { e.preventDefault(); buscar(); }}>
          <label className="flex flex-col gap-1 text-meta font-semibold text-ink-soft w-full sm:w-64">
            Número de caso
            <Input value={numero} onChange={(e) => setNumero(e.target.value)} placeholder="PE-011-2026" />
          </label>
          <Button type="submit" icon={Search} disabled={buscando || numero.trim().length < 2}>
            {buscando ? 'Buscando…' : 'Buscar'}
          </Button>
        </form>
        {fallo && <div className="mt-4"><Alert tone="danger" title="No se pudo buscar" detalleTecnico={fallo.detalleTecnico}>{fallo.message}</Alert></div>}
        {resultado && resultado.casos.length === 0 && (
          <p className="mt-4 text-body text-ink-soft">Ningún caso de tu ámbito lleva ese número.</p>
        )}
        {/* El servidor corta en `tope` coincidencias; si quedaron otras fuera, se dice. */}
        {resultado?.hay_mas && (
          <div className="mt-4">
            <Alert tone="warning" icon={AlertTriangle} data-testid="regulatorio-todos-hay-mas">
              Se muestran los primeros <Mono className="text-ink">{resultado.tope}</Mono> resultados.
              Escribe más del número para acotar la búsqueda.
            </Alert>
          </div>
        )}
        {resultado && resultado.casos.length > 0 && (
          <ul className="mt-4 divide-y divide-hairline">
            {paginar(resultado.casos, paginaDeResultados).map((c) => (
              <li key={c.caso_id} className="flex flex-wrap items-center gap-3 py-3">
                <Codigo className="whitespace-nowrap">{c.numero}</Codigo>
                <span className="inline-flex items-center gap-1.5 text-compacto"><Bandera cod={c.pais} nombre={c.nombre_pais} />{c.nombre_pais}</span>
                {/* Bug #1083: los destinos de hoy; la autoridad de la apertura solo sin ellos. */}
                <span className="text-compacto">{c.texto_de_los_destinos ?? c.autoridad ?? '—'}</span>
                <span className="text-compacto">{c.producto ?? '—'}</span>
                {/* Un NO GRAVE no tiene ruta regulatoria (RN-15: no se notifica
                    individualmente), así que no hay carta que preparar. Desde que la
                    búsqueda alcanza también a los no graves (bug 702), decir «Carta sin
                    preparar» ahí anunciaba trabajo pendiente que no existe. */}
                <StatusBadge tone={tonoDeLaFilaEncontrada(c)}>
                  {estadoDeLaFilaEncontrada(c)}
                </StatusBadge>
                {onAbrir && (
                  <span className="ml-auto">
                    <Button size="sm" variant="secondary" icon={FolderOpen} onClick={() => onAbrir(c.caso_id)}>
                      Abrir en solo lectura
                    </Button>
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
        {resultado && resultado.casos.length > POR_PAGINA_DE_LISTADOS && (
          <div className="mt-2">
            <Paginacion pagina={paginaDeResultados} total={resultado.casos.length}
              enEstaPagina={paginar(resultado.casos, paginaDeResultados).length} onPagina={setPaginaDeResultados}
              etiqueta="Paginación de los resultados" sustantivo={['resultado', 'resultados']}
              data-testid="regulatorio-todos-paginacion" />
          </div>
        )}
      </Card>
    </>
  );
}

// ---- El trámite del caso ------------------------------------------------------------------

/** Los seis pasos del trámite ante la autoridad, en el orden del procedimiento. */
const PASOS_DEL_TRAMITE = ['Formato Amarillo', 'Carta', 'Firma', 'Presentación', 'Cargo', 'FAR-03'];

/** Lo que una carta aporta al paso del trámite: su estado, su cargo y su remisión del FAR-03. */
type CartaDelPaso = Pick<Tramite, 'estado_carta' | 'estado_cargo' | 'fecha_remision_informe_final'>;

/**
 * En qué paso está el trámite (índice de `PASOS_DEL_TRAMITE`; 6 = todo hecho). El Formato
 * Amarillo es del trámite; lo demás, de `carta` (por omisión, la raíz: la primera carta).
 */
export const pasoDelTramite = (t: Tramite, carta: CartaDelPaso = t): number => {
  if (t.estado_formato_amarillo !== 'completo') return 0;
  if (carta.estado_carta === 'sin_preparar') return 1;
  if (carta.estado_carta === 'preparada' || carta.estado_carta === 'esperando_firma') return 2;
  if (carta.estado_carta === 'firmada') return 3;
  if (carta.estado_cargo !== 'recibido') return 4;
  if (!carta.fecha_remision_informe_final) return 5;
  return 6;
};

/**
 * HU-22-C5 · El cierre del caso. Lista lo que falta con su control (C-04/C-06/C-07/C-08)
 * y solo deja cerrar cuando el servidor dice que se puede. Cerrar caduca las invitaciones
 * de los especialistas (RN-31), y se avisa antes.
 */
function CierreDelCaso({ casoId, onCerrado }: { casoId: string; onCerrado: () => void }) {
  const [estado, setEstado] = useState<EstadoDelCierre | null>(null);
  const [fallo, setFallo] = useState<Fallo | null>(null);
  const [confirmando, setConfirmando] = useState(false);
  const [observacion, setObservacion] = useState('');
  const [ocupado, setOcupado] = useState(false);
  const [cerrado, setCerrado] = useState(false);

  // Refresca la lista de controles SIN borrar el fallo que la motivó (el 422 del cierre).
  const cargar = useCallback(async () => {
    try {
      setEstado(await estadoDelCierre(casoId));
    } catch (e) {
      setFallo(aFallo(e));
    }
  }, [casoId]);

  useEffect(() => {
    let vivo = true;
    estadoDelCierre(casoId).then((e) => { if (vivo) setEstado(e); }).catch((e) => { if (vivo) setFallo(aFallo(e)); });
    return () => { vivo = false; };
  }, [casoId]);

  const cerrar = async () => {
    setFallo(null);
    setOcupado(true);
    try {
      // HU-27-C7 (AT S3 §2.0): el «cerrado por» sale de la sesión; mandarlo es 422.
      await cerrarCaso(casoId, { observacion: observacion.trim() || null });
      setCerrado(true);
      setConfirmando(false);
      onCerrado();
    } catch (e) {
      const f = aFallo(e);
      setFallo(f);
      setConfirmando(false);
      // El 422 trae la lista actualizada de lo que falta: se refresca lo que se ve.
      if (f.estado === 422) cargar();
    } finally {
      setOcupado(false);
    }
  };

  // El 422 del servidor puede traer la lista en el cuerpo; se muestra tal cual.
  const condicionesDelFallo = (fallo?.cuerpo as { pendientes?: { condicion: string; detalle: string; control: string | null }[] } | null)?.pendientes;

  return (
    <Card className="mt-4" title="Cierre del caso" icon={Lock}
      subtitle="Se cierra cuando todos los controles del proceso están cumplidos. Cerrar no borra nada: el expediente queda consultable.">
      {cerrado && <div className="mb-3"><Alert tone="success" title="Caso cerrado">Las invitaciones de especialistas caducaron. El expediente sigue disponible en solo lectura.</Alert></div>}
      {fallo && (
        <div className="mb-3">
          <Alert tone={fallo.estado === 422 || fallo.estado === 409 ? 'warning' : 'danger'} title={fallo.estado === 422 || fallo.estado === 409 ? 'Todavía no se puede cerrar' : 'No se pudo consultar el cierre'} detalleTecnico={fallo.detalleTecnico}>
            {fallo.message}
            {condicionesDelFallo && condicionesDelFallo.length > 0 && (
              <ul className="list-disc pl-4 mt-1">
                {condicionesDelFallo.map((c) => <li key={c.condicion}>{c.detalle}</li>)}
              </ul>
            )}
          </Alert>
        </div>
      )}
      {!estado && !fallo && <p className="text-body text-ink-soft">Consultando los controles…</p>}
      {estado && (
        <>
          {estado.condiciones_pendientes.length === 0
            ? <p className="text-body inline-flex items-center gap-1.5"><CheckCircle2 size={15} className="text-emerald-600" /> Todos los controles están cumplidos.</p>
            : (
              <ul className="space-y-1.5" aria-label="Condiciones pendientes para cerrar">
                {estado.condiciones_pendientes.map((c) => (
                  <li key={c.condicion} className="flex flex-wrap items-start gap-2 text-body">
                    <AlertTriangle size={14} className="text-amber-600 shrink-0 mt-0.5" aria-hidden="true" />
                    <span className="min-w-0 flex-1">{c.detalle}</span>
                  </li>
                ))}
              </ul>
            )}
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <Button icon={Lock} onClick={() => setConfirmando(true)} disabled={!estado.se_puede_cerrar || ocupado || cerrado}>Cerrar caso</Button>
            {estado.veces_reabierto > 0 && <span className="text-meta text-ink-soft">Reabierto {estado.veces_reabierto} {estado.veces_reabierto === 1 ? 'vez' : 'veces'}.</span>}
            {estado.estado_seguimiento && <span className="text-meta text-ink-soft">Seguimiento: {estado.estado_seguimiento}.</span>}
          </div>
        </>
      )}
      <Modal open={confirmando} onClose={() => setConfirmando(false)} tone="warning" icon={Lock}
        eyebrow="Asuntos Regulatorios · Cierre del caso" title="¿Cerrar el caso?"
        footer={<>
          <Button variant="secondary" onClick={() => setConfirmando(false)}>Cancelar</Button>
          <Button variant="danger" icon={Lock} onClick={() => { cerrar(); }} disabled={ocupado}>Cerrar caso</Button>
        </>}>
        <Alert tone="warning">Al cerrar, las invitaciones de especialistas caducan.</Alert>
        <Field label="Observación" hint="Opcional. Queda en el historial del caso con quién cerró y cuándo.">
          <Textarea aria-label="Observación del cierre" rows={2} value={observacion} onChange={(e) => setObservacion(e.target.value)} />
        </Field>
      </Modal>
    </Card>
  );
}

/**
 * C5 · HU-24-C12: el trámite del caso, paso a paso. Muestra los relojes, el stepper, el
 * estado de cada pieza y, cuando el paso es «Remitir el informe final», el registro de la
 * remisión del FAR-03 a la autoridad. Los demás pasos se abren desde aquí en su pantalla
 * (Formato Amarillo, carta), y al final está el cierre del caso.
 */
function TramiteDelCaso({ fila, onVolver, onCambio, onFormatoAmarillo, onCarta }: {
  fila: FilaRegulatoria; onVolver: () => void; onCambio: () => void; onFormatoAmarillo?: () => void; onCarta?: () => void;
}) {
  const [tramite, setTramite] = useState<Tramite | null>(null);
  const [fallo, setFallo] = useState<Fallo | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  // RN-82: las fechas del trámite, en la zona del país del caso; la remisión nace con SU hoy.
  const zona = useZonaDelPais()(fila.pais);
  const [fechaRemision, setFechaRemision] = useState(() => fechaDeHoyEnZona(zona));
  const [ocupado, setOcupado] = useState(false);
  const [hecho, setHecho] = useState(false);

  useEffect(() => {
    let vivo = true;
    verTramite(fila.caso_id).then((t) => { if (vivo) setTramite(t); }).catch((e) => { if (vivo) setFallo(aFallo(e)); });
    return () => { vivo = false; };
  }, [fila.caso_id]);

  const remitir = async () => {
    setFallo(null);
    setOcupado(true);
    try {
      setTramite(await registrarRemisionDelInformeFinal(fila.caso_id, fechaRemision));
      setHecho(true);
      onCambio();
    } catch (e) {
      setFallo(aFallo(e));
    } finally {
      setOcupado(false);
    }
  };

  // HU-20-C5/C6 (observación del operador, 21-sep-2026): el trámite avisaba «Carta
  // desactualizada» pero su única acción era «Solicitar la firma», que abre la carta; el
  // botón de regenerar solo existía dentro de esa pantalla y nadie lo encontraba. Se
  // ofrece aquí, en el mismo aviso, y solo si el servidor lo admite (`acciones_de_la_carta`
  // trae «regenerar» mientras la carta está preparada y sin firma).
  const regenerar = async () => {
    setFallo(null);
    setAviso(null);
    setOcupado(true);
    try {
      const t = await prepararCarta(fila.caso_id, true);
      setTramite(t);
      const conPlantilla = t.plantilla_usada ? ` y la plantilla ${t.plantilla_usada}` : '';
      setAviso(`Carta v${t.carta_version} regenerada con el Formato Amarillo v${t.formato_amarillo_version ?? ''}${conPlantilla}. La anterior queda en el historial.`);
      onCambio();
    } catch (e) {
      setFallo(aFallo(e));
    } finally {
      setOcupado(false);
    }
  };

  const remitido = tramite?.fecha_remision_informe_final ?? null;
  const acciones = new Set(tramite?.acciones_de_la_carta ?? []);
  // CC-02 (B11, `ui/MG-ILE-1075.md`): con varias cartas la raíz es solo la primera; el estado, la
  // desactualización y la remisión se leen por carta y se regeneran en la pantalla de la carta.
  const cartas = tramite ? cartasActivas(tramite) : [];
  const varias = cartas.length > 1;
  const desactualizada = !varias && Boolean(tramite?.carta_desactualizada);
  // Bug #1082 (V-04 de `ui/MG-ILE-1075.md`): con varias cartas el paso a paso sigue a la carta
  // más urgente, la que el servidor eligió para la fila (`autoridad_codigo`, B10) y de la que
  // salen los relojes de arriba. Con una carta, o si no está entre las activas, la raíz.
  const cartaDelPaso = varias ? cartas.find((c) => c.autoridad_codigo === fila.autoridad_codigo) : undefined;
  const tomar = (t: Tramite) => { setTramite(t); onCambio(); };

  return (
    <>
      <PageHeader
        eyebrow="Asuntos Regulatorios · Trámite del caso"
        hu="HU-50 · C5 / HU-24 · C12 / HU-22 · C5"
        title={fila.texto_de_la_accion ?? 'Trámite regulatorio'}
        subtitle={
          <span className="inline-flex flex-wrap items-center gap-2">
            <Bandera cod={fila.pais} nombre={fila.nombre_pais} />
            <Codigo className="whitespace-nowrap">{fila.numero ?? fila.caso_id.slice(0, 8)}</Codigo>
            {fila.autoridad && <StatusBadge tone="info">{fila.autoridad}</StatusBadge>}
          </span>
        }
        icon={Send}
        actions={<Button variant="secondary" size="sm" icon={ArrowLeft} onClick={onVolver}>Volver a la bandeja</Button>}
      />
      {tramite && (
        <div className="mb-4">
          <Stepper pasos={PASOS_DEL_TRAMITE} actual={pasoDelTramite(tramite, cartaDelPaso)} aria-label="Pasos del trámite" />
        </div>
      )}
      {fallo && <div className="mb-3"><Alert tone="danger" title="No se pudo completar" detalleTecnico={fallo.detalleTecnico}>{fallo.message}</Alert></div>}
      {aviso && <div className="mb-3"><Alert tone="success" title={aviso} onCerrar={() => setAviso(null)} /></div>}
      {tramite && desactualizada && (
        <div className="mb-3">
          <Alert tone="warning" title={MENSAJE_CARTA_DESACTUALIZADA} icon={AlertTriangle}>
            <p>
              La carta v{tramite.carta_version} se generó con el Formato Amarillo v{formatoAmarilloDeLaCartaVigente(tramite) ?? '—'}; el trámite ya va por
              el v{tramite.formato_amarillo_version}. Regenérala antes de pedir la firma: nace la v{tramite.carta_version + 1} con la corrección y la
              anterior queda en el historial.
            </p>
            {acciones.has('regenerar') && (
              <div className="mt-2">
                <Button size="sm" icon={RefreshCw} onClick={() => { regenerar(); }} disabled={ocupado}>
                  Regenerar carta (v{tramite.carta_version + 1})
                </Button>
              </div>
            )}
          </Alert>
        </div>
      )}
      <Card title="Los relojes del caso" subtitle="Plazo de la notificación y del informe final.">
        <div className="flex flex-wrap gap-6">
          <RelojDelPlazo etiqueta="Notificación" reloj={fila.notificacion} zonaHoraria={zona} />
          {fila.informe_final && <RelojDelPlazo etiqueta="Informe final" reloj={fila.informe_final} zonaHoraria={zona} />}
        </div>
      </Card>
      {/* HU-19: el Formato Amarillo es el primer paso; se abre desde aqui (C5: el paso
          «Completar el Formato Amarillo» lleva a esa tarea). */}
      {(onFormatoAmarillo || onCarta) && (
        <div className="mt-4 flex flex-wrap gap-2">
          {onFormatoAmarillo && (
            <Button variant={fila.accion === 'completar_formato_amarillo' ? 'primary' : 'secondary'} icon={FileCheck2} onClick={onFormatoAmarillo}>
              {fila.accion === 'completar_formato_amarillo' ? 'Completar el Formato Amarillo' : 'Ver el Formato Amarillo'}
            </Button>
          )}
          {/* HU-20: la carta es el segundo paso; «Preparar la carta» lleva a su pantalla
              (C3), y desde cualquier otro paso se puede ver y regenerar (C5). Con la carta
              desactualizada el paso sigue ahí, pero lo primario es regenerar (arriba). */}
          {onCarta && (
            <Button variant={PASOS_DE_LA_CARTA[fila.accion ?? ''] && !desactualizada ? 'primary' : 'secondary'} icon={FileText} onClick={onCarta}>
              {PASOS_DE_LA_CARTA[fila.accion ?? ''] ?? 'Ver la carta de notificación'}
            </Button>
          )}
        </div>
      )}
      {/* BUG-O11 (O-11, 29-sep-2026): la carta y el acuse se leen de `texto_de_la_carta` y
          `texto_del_acuse` tal cual; antes el acuse se traducía desde `estado_cargo` y decía
          «Acuse pendiente» cuando la fila de esta misma bandeja decía «Acuse demorado». */}
      <Card className="mt-4" title="Estado del trámite">
        {!tramite && !fallo && <p className="text-body text-ink-soft">Abriendo el trámite…</p>}
        {tramite && (
          <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 text-body" aria-label="Estado del trámite">
            <dt className="text-ink-soft">Formato Amarillo</dt><dd>{etiquetaDelFormatoAmarillo(tramite.estado_formato_amarillo)}</dd>
            {varias && <FilasDeLasCartas cartas={cartas} zonaHoraria={zona} />}
            {!varias && (
              <>
              <dt className="text-ink-soft">Carta</dt>
              <dd>
                {tramite.texto_de_la_carta}
                {tramite.carta_desactualizada && <span className="block text-meta text-amber-700">{MENSAJE_CARTA_DESACTUALIZADA}</span>}
              </dd>
              {tramite.texto_del_acuse && (
                <>
                  <dt className="text-ink-soft">Acuse de la autoridad</dt>
                  <dd><EstadoDelAcuse acuse={tramite} /></dd>
                </>
              )}
              <dt className="text-ink-soft">Presentada el</dt><dd><Mono>{fecha(tramite.fecha_presentacion, zona)}</Mono></dd>
              <dt className="text-ink-soft">FAR-03 remitido a la ANC</dt><dd><Mono>{remitido ?? '—'}</Mono></dd>
              </>
            )}
          </dl>
        )}
      </Card>
      {fila.accion === 'remitir_el_informe_final' && (
        <Card className="mt-4" title="Registrar remisión del FAR-03" icon={FileCheck2}
          subtitle="Registra cuándo enviaste el informe final a la autoridad.">
          {varias && <RemisionPorCarta casoId={fila.caso_id} cartas={cartas} zonaHoraria={zona} onTramite={tomar} />}
          {!varias && (remitido
            ? <Alert tone="success" title={`FAR-03 remitido a la ANC · ${remitido}`}>{hecho ? 'El plazo del informe final queda cumplido.' : 'La remisión ya estaba registrada.'}</Alert>
            : (
              <div className="flex flex-wrap items-end gap-3">
                <Field label="Fecha de remisión" required>
                  <Input aria-label="Fecha de remisión" type="date" value={fechaRemision} onChange={(e) => setFechaRemision(e.target.value)} />
                </Field>
                <Button icon={FileCheck2} onClick={() => { remitir(); }} disabled={ocupado || !fechaRemision}>Registrar remisión del FAR-03</Button>
              </div>
            ))}
        </Card>
      )}
      <CierreDelCaso casoId={fila.caso_id} onCerrado={onCambio} />
    </>
  );
}

/** Una carta esperando la firma del PRFV: la fila de su trámite, su autoridad y sus días. */
type EsperaDeFirmaEnLaBandeja = { fila: FilaRegulatoria; autoridad: string | null; codigo: string | null; dias: number; varias: boolean };

/**
 * CC2-50-C2: la cola es POR CARTA. Cada carta que espera la firma es una fila, con la autoridad
 * y los días hábiles que trae el servidor; un trámite sin `cartas` (servidor anterior a CC-02)
 * aporta su fila de siempre.
 */
const esperasDeFirma = (filas: FilaRegulatoria[]): EsperaDeFirmaEnLaBandeja[] =>
  filas.flatMap((f) => {
    if (f.cartas && f.cartas.length > 0) {
      return f.cartas
        .filter((c) => c.estado_carta === 'esperando_firma')
        .map((c) => ({
          fila: f, autoridad: c.autoridad, codigo: c.autoridad_codigo, dias: c.dias_esperando_firma ?? 0, varias: (f.cartas?.length ?? 0) > 1,
        }));
    }
    return f.estado_carta === 'esperando_firma'
      ? [{ fila: f, autoridad: f.autoridad, codigo: null, dias: f.dias_esperando_firma ?? 0, varias: false }]
      : [];
  });

/** HU-21-C8: «Esperando firma del PRFV (3)» con cada carta y sus días de espera: 1, 3, 6. */
function ColaDeFirma({ filas, onAbrir }: { filas: FilaRegulatoria[]; onAbrir: (fila: FilaRegulatoria) => void }) {
  const esperas = esperasDeFirma(filas);
  if (esperas.length === 0) return null;
  const ordenadas = [...esperas].sort((a, b) => b.dias - a.dias);
  return (
    <Card className="mt-4" title={`Esperando firma del PRFV (${esperas.length})`} icon={Hourglass}
      subtitle="Primero los que llevan más tiempo esperando.">
      <ul className="divide-y divide-hairline" aria-label="Esperando firma del PRFV">
        {ordenadas.map(({ fila: f, autoridad, codigo, dias, varias }) => (
          <li key={`${f.caso_id}:${codigo ?? ''}`} className="py-2 flex flex-wrap items-center gap-3 text-body"
            data-testid="bandeja-reg-cola-fila" data-autoridad={codigo ?? ''}>
            <Bandera cod={f.pais} nombre={f.nombre_pais} />
            <Codigo className="whitespace-nowrap">{f.numero ?? f.caso_id.slice(0, 8)}</Codigo>
            <span className="text-ink-soft">{autoridad ?? f.nombre_pais}</span>
            <StatusBadge tone={dias >= 5 ? 'danger' : 'info'} icon={Hourglass}>
              {dias} {dias === 1 ? 'día hábil' : 'días hábiles'}
            </StatusBadge>
            <Button size="sm" variant="ghost" onClick={() => onAbrir(f)}
              aria-label={varias ? `Abrir ${f.numero ?? 'el caso'} · ${autoridad ?? codigo ?? ''}` : undefined}>
              Abrir
            </Button>
          </li>
        ))}
      </ul>
    </Card>
  );
}

const SIN_FILTROS: FiltrosRegulatorios = { autoridad: '', accion: '', estado_plazo: '', producto: '', desde: '', hasta: '', numero: '' };

/** Desde dónde se abrió una tarea: decide a dónde vuelve «Volver». */
type Origen = 'bandeja' | 'tramite';

export default function BandejaRegulatoria({ onAbrirCaso, actor = '', puedeCompletarEnElCaso = true }: {
  /** Abre el caso en SOLO LECTURA (el expediente). */
  onAbrirCaso?: (casoId: string) => void;
  /** Quién presenta la carta, registra el cargo o cierra el caso (el nombre de la sesión). */
  actor?: string;
  /** Si el rol escribe en el caso lo que el Formato Amarillo pide y el Verde no captura (ver `FormatoAmarillo`). */
  puedeCompletarEnElCaso?: boolean;
}) {
  // LOS FILTROS SOBREVIVEN A ABRIR EL EXPEDIENTE Y VOLVER (bug 683 · CP-50-01 p.12). Abrir
  // el caso en solo lectura cambia de módulo: `App` monta el expediente y esta pantalla se
  // desmonta entera, así que con `useState(SIN_FILTROS)` la bandeja volvía en blanco. Mismo
  // helper —y mismo almacén— que la bandeja del Comité, con sus filtros aparte de los suyos:
  // ver `lib/filtrosPersistidos.ts`.
  const [filtros, setFiltros] = useState<FiltrosRegulatorios>(() => filtrosIniciales('regulatorio', SIN_FILTROS));
  const [bandeja, setBandeja] = useState<Bandeja | null>(null);
  const [fallo, setFallo] = useState<Fallo | null>(null);
  const [cargando, setCargando] = useState(true);
  const [viendoTodos, setViendoTodos] = useState(false);
  // La vista del trámite (relojes, pasos, cierre) y las dos tareas con pantalla propia.
  const [tarea, setTarea] = useState<FilaRegulatoria | null>(null);
  const [formatoDe, setFormatoDe] = useState<{ fila: FilaRegulatoria; origen: Origen } | null>(null);
  const [cartaDe, setCartaDe] = useState<{ fila: FilaRegulatoria; origen: Origen } | null>(null);
  // La página de cada bloque, por su clave. Vive aquí y no en el bloque: sobrevive a abrir el
  // trámite y volver, y cada carga de la bandeja (un filtro nuevo, «Refrescar», volver de la
  // carta o del Formato Amarillo) la devuelve a la primera de un solo golpe.
  const [paginas, setPaginas] = useState<Partial<Record<ClaveDeBloqueRegulatorio, number>>>({});
  const irAPagina = useCallback((clave: ClaveDeBloqueRegulatorio, pagina: number) => {
    setPaginas((p) => ({ ...p, [clave]: pagina }));
  }, []);

  // D3 · texto con 300 ms de gracia; el resto al instante.
  const productoDiferido = useDiferido(filtros.producto ?? '');
  const numeroDiferido = useDiferido(filtros.numero ?? '');
  // Campo a campo en las dependencias: con el objeto entero, cada tecla re-consultaba.
  const { autoridad, accion, estado_plazo: estadoPlazo, desde, hasta } = filtros;
  const filtrosEfectivos = useMemo<FiltrosRegulatorios>(
    () => ({ autoridad, accion, estado_plazo: estadoPlazo, desde, hasta, producto: productoDiferido, numero: numeroDiferido }),
    [autoridad, accion, estadoPlazo, desde, hasta, productoDiferido, numeroDiferido],
  );

  // Un rango de fechas al revés no se consulta: se dice bajo el campo.
  const fechasInvertidas = rangoInvertido(filtrosEfectivos.desde, filtrosEfectivos.hasta);
  // D3 · token de secuencia: solo la última consulta lanzada escribe en pantalla.
  const secuencia = useRef(0);
  const cargar = useCallback(async () => {
    if (fechasInvertidas) return;
    const mia = ++secuencia.current;
    setCargando(true);
    setFallo(null);
    try {
      const respuesta = await consultarBandejaRegulatoria(filtrosEfectivos);
      if (mia !== secuencia.current) return;
      setBandeja(respuesta);
      setPaginas({});
    } catch (e) {
      if (mia !== secuencia.current) return;
      setFallo(aFallo(e));
    } finally {
      if (mia === secuencia.current) setCargando(false);
    }
  }, [filtrosEfectivos, fechasInvertidas]);

  useEffect(() => { cargar(); }, [cargar]);

  // Se guarda lo QUE SE VE en los campos —no los diferidos—: al volver, la pantalla tiene
  // que aparecer tal cual se dejó. Quitar los filtros también se guarda: si se quitaron a
  // propósito, volver no debe resucitarlos.
  useEffect(() => { guardarFiltros('regulatorio', filtros); }, [filtros]);

  const hayFiltro = useMemo(() => Object.values(filtros).some(Boolean), [filtros]);
  // Cuántos filtros hay puestos, para la tarjeta plegada: el rango «Recibido» cuenta como uno
  // aunque tenga desde y hasta; el buscador por número cuenta si tiene texto.
  const filtrosActivos = useMemo(() => (
    [filtros.autoridad, filtros.accion, filtros.estado_plazo, filtros.producto, filtros.numero].filter(Boolean).length
    + (filtros.desde || filtros.hasta ? 1 : 0)
  ), [filtros]);
  const fijar = (parte: Partial<FiltrosRegulatorios>) => setFiltros((f) => ({ ...f, ...parte }));

  /** C5: la acción de la fila abre directamente la tarea que la resuelve. */
  const abrirAccion = useCallback((fila: FilaRegulatoria) => {
    const destino: Tarea = fila.accion ? TAREA_DE[fila.accion] : 'tramite';
    if (destino === 'formato') setFormatoDe({ fila, origen: 'bandeja' });
    else if (destino === 'carta') setCartaDe({ fila, origen: 'bandeja' });
    else setTarea(fila);
  }, []);
  const abrirTramite = useCallback((fila: FilaRegulatoria) => setTarea(fila), []);

  // C20 y C5: las vistas de «todos los casos» y de las tareas se montan encima; la bandeja
  // (filtros y datos) sigue viva debajo y al volver está tal cual.
  if (viendoTodos) {
    return <TodosLosCasos onAbrir={onAbrirCaso} onVolver={() => setViendoTodos(false)} />;
  }
  if (formatoDe) {
    const { fila, origen } = formatoDe;
    return (
      <FormatoAmarillo casoId={fila.caso_id} onAbrirCaso={onAbrirCaso} textoDeVolver={origen === 'tramite' ? 'Volver al trámite' : 'Volver a la bandeja'}
        puedeCompletarEnElCaso={puedeCompletarEnElCaso}
        onVolver={() => { setFormatoDe(null); if (origen === 'tramite') setTarea(fila); cargar(); }} />
    );
  }
  if (cartaDe) {
    const { fila, origen } = cartaDe;
    return (
      <CartaDeNotificacion casoId={fila.caso_id} numero={fila.numero} pais={fila.pais} actor={actor}
        textoDeVolver={origen === 'tramite' ? 'Volver al trámite' : 'Volver a la bandeja'}
        onVolver={() => { setCartaDe(null); if (origen === 'tramite') setTarea(fila); cargar(); }} onCambio={() => { cargar(); }} />
    );
  }
  if (tarea) {
    return (
      <TramiteDelCaso
        fila={tarea}
        onVolver={() => setTarea(null)}
        onCambio={() => { cargar(); }}
        onFormatoAmarillo={() => { setFormatoDe({ fila: tarea, origen: 'tramite' }); setTarea(null); }}
        onCarta={() => { setCartaDe({ fila: tarea, origen: 'tramite' }); setTarea(null); }}
      />
    );
  }

  const encabezado: ReactNode = bandeja && !cargando && (
    <p className="text-meta text-ink-soft" aria-live="polite">
      <Mono className="text-ink">{bandeja.total}</Mono> {bandeja.total === 1 ? 'caso' : 'casos'}
      {bandeja.filtrado_por && <> · filtrado por: <span className="text-ink">{bandeja.filtrado_por}</span></>}
    </p>
  );

  return (
    <>
      <PageHeader
        eyebrow="Asuntos Regulatorios · Bandeja"
        hu="HU-50 · HU-24 · RN-20 / RN-21 / RN-35 / RN-39"
        title="Notificación regulatoria"
        subtitle="Los casos graves con trámite ante la autoridad, en el orden en que hay que atenderlos. Cada fila dice qué paso toca dar."
        icon={Send}
        actions={
          <>
            <Button variant="secondary" size="sm" icon={Search} onClick={() => setViendoTodos(true)}>
              Ver todos los casos
            </Button>
            <Button variant="secondary" size="sm" icon={RefreshCw} onClick={() => { cargar(); }} disabled={cargando}>
              Refrescar
            </Button>
          </>
        }
      />

      {/* C14 – C18: los filtros. Se combinan con Y (C16d). Plegables y abiertos al entrar
          (operador, 5-oct-2026). */}
      <FiltrosColapsables className="mb-4" data-testid="regulatorio-filtros" activos={filtrosActivos} actions={hayFiltro && (
        <Button size="sm" variant="ghost" icon={X} onClick={() => setFiltros(SIN_FILTROS)}>Quitar filtros</Button>
      )}>
        <div className="grid grid-cols-[repeat(auto-fill,minmax(min(100%,16rem),1fr))] gap-3">
          <Select
            aria-label="Filtrar por autoridad"
            placeholder="Todas las autoridades"
            value={filtros.autoridad ?? ''}
            onChange={(e) => fijar({ autoridad: String(e.target.value ?? '') })}
            options={(bandeja?.autoridades ?? []).map((a) => ({ value: a, label: a }))}
          />
          <Select
            aria-label="Filtrar por acción pendiente"
            placeholder="Cualquier acción pendiente"
            value={filtros.accion ?? ''}
            onChange={(e) => fijar({ accion: String(e.target.value ?? '') as AccionRegulatoria | '' })}
            options={ACCIONES_REGULATORIAS}
          />
          <Select
            aria-label="Filtrar por estado del plazo"
            placeholder="Cualquier estado del plazo"
            value={filtros.estado_plazo ?? ''}
            onChange={(e) => fijar({ estado_plazo: String(e.target.value ?? '') as EstadoDelReloj | '' })}
            options={ESTADOS_DEL_RELOJ}
          />
          <Input aria-label="Filtrar por producto" placeholder="Producto" value={filtros.producto ?? ''}
            onChange={(e) => fijar({ producto: e.target.value })} />
          {/* Un solo calendario de rango para «Recibido» (operador, 5-oct-2026): antes eran dos
              fechas sueltas sin «desde» ni «hasta» a la vista. */}
          <RangoDeFechas aria-label="Recibido" prefijo="Recibido" placeholder="Recibido: cualquier fecha" data-testid="regulatorio-filtro-recibido"
            desde={filtros.desde ?? ''} hasta={filtros.hasta ?? ''} aria-invalid={fechasInvertidas || undefined}
            aria-describedby={fechasInvertidas ? 'rango-de-fechas-ar' : undefined}
            onCambio={({ desde, hasta }) => fijar({ desde, hasta })} />
        </div>
        {fechasInvertidas && <p id="rango-de-fechas-ar" role="alert" className="mt-1 text-meta text-rose-700">{MENSAJE_RANGO_INVERTIDO}</p>}
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <Input aria-label="Buscar por número de caso" placeholder="Buscar por N.º de caso (PE-010-2026)"
            className="!w-80 max-w-full" value={filtros.numero ?? ''} onChange={(e) => fijar({ numero: e.target.value })} />
          {encabezado}
        </div>
      </FiltrosColapsables>

      {fallo && (
        <Alert tone="danger" title="No se pudo cargar la bandeja" detalleTecnico={fallo.detalleTecnico}>
          {fallo.message}
          {fallo.estado === 0 && ' Revisa tu conexión e intenta refrescar.'}
        </Alert>
      )}

      {cargando && !bandeja && <p className="p-6 text-body text-ink-soft">Cargando la bandeja…</p>}

      {/* EL RECORTE SE DICE, NO SE CALLA (operador, 5-oct-2026), como en la bandeja del Comité.
          La consulta tiene un tope —una válvula, no una regla de negocio— sobre los casos con
          trámite PENDIENTE, del de recepción más antigua al más reciente. Antes cortaba sin
          avisar. Los filtros de esta pantalla se aplican sobre lo traído, así que no recuperan
          lo que quedó fuera: lo que toca es avisar. En la operación normal no aparece. */}
      {!!bandeja?.candidatos_fuera_del_tope && (
        <div className="mb-3">
          <Alert tone="warning" title="La bandeja no está completa" icon={AlertTriangle} data-testid="regulatorio-fuera-del-tope">
            Hay <Mono className="text-ink">{bandeja.candidatos_fuera_del_tope}</Mono>{' '}
            {bandeja.candidatos_fuera_del_tope === 1 ? 'caso con trámite pendiente que no entra' : 'casos con trámite pendiente que no entran'} en
            esta bandeja, y son los de recepción más reciente. Avisa al administrador del sistema.
          </Alert>
        </div>
      )}

      {/* C2, C17: los bloques NO VACÍOS, en el orden que manda el servidor. */}
      {bandeja?.bloques.map((b) => (
        <Bloque key={b.clave} bloque={b} pagina={paginas[b.clave] ?? 1} onPagina={irAPagina}
          onAccion={abrirAccion} onTramite={abrirTramite} onAbrir={onAbrirCaso} />
      ))}

      {/* HU-21-C8: la espera de firma agrupada, con los días HÁBILES de cada caso. Mira TODAS
          las filas de todos los bloques, no solo la página que se ve. */}
      <ColaDeFirma filas={bandeja?.bloques.flatMap((b) => b.filas) ?? []} onAbrir={abrirTramite} />

      {/* C21: sin nada pendiente NO hay tabla vacía: se explica y «Ver todos los casos» sigue a la vista. */}
      {bandeja && bandeja.total === 0 && !fallo && (
        <Card pad={false}>
          <EmptyState
            icon={hayFiltro ? TimerOff : Inbox}
            title={bandeja.mensaje_de_vacia ?? 'No tienes casos con trámite regulatorio pendiente'}
            description={hayFiltro
              ? 'Prueba con otro filtro o quítalos todos.'
              : 'Los casos con la notificación concluida y el FAR-03 remitido siguen disponibles desde «Ver todos los casos» y en «Bandeja».'}
            action={hayFiltro
              ? <Button variant="secondary" icon={X} onClick={() => setFiltros(SIN_FILTROS)}>Quitar filtros</Button>
              : <Button variant="secondary" icon={Search} onClick={() => setViendoTodos(true)}>Ver todos los casos</Button>}
          />
        </Card>
      )}
    </>
  );
}
